// EmpresaOnboarding — cliente adiciona CNPJs/CPFs a uma organização existente.
// Standalone, etapa única, 1 ou vários cadastros. O aceite persiste de verdade
// via POST /invitations/<token>/accept (seta cookies httpOnly); autofill de CNPJ
// usa o lookup real da Receita.
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { Button, Icon } from "@/components/common";
import { useCnpjLookup } from "@/hooks/useCnpjLookup";
import { extractErrorMessage } from "@/services/axiosConfig";
import { invitationService } from "@/services/invitationService";
import type { ClientAcceptPayload } from "@/types/invitation";
import { formatCnpj, formatCpf, stripDocument } from "@/utils/fmt";

interface Props {
  token?: string;
  orgName?: string;
  onExit?: () => void;
}

type DocType = "cnpj" | "cpf";

interface Row {
  id: string;
  docType: DocType;
  cnpj: string;
  cpf: string;
  razao: string;
  fantasia: string;
  cidade: string;
  uf: string;
  cnae: string;
  regime: string;
  // Endereço estruturado (do lookup; não exibido na linha — editável no detalhe).
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cep: string;
  nome: string;
  loading: boolean;
  autofilled: boolean;
}

const newRow = (id: string, docType: DocType = "cnpj"): Row => ({
  id,
  docType,
  cnpj: "",
  cpf: "",
  razao: "",
  fantasia: "",
  cidade: "",
  uf: "",
  cnae: "",
  regime: "",
  logradouro: "",
  numero: "",
  complemento: "",
  bairro: "",
  cep: "",
  nome: "",
  loading: false,
  autofilled: false,
});

export function ClientOnboarding({ token, orgName, onExit }: Props) {
  const orgDisplayName = orgName || "Sua organização";
  const cnpjLookup = useCnpjLookup();

  const [count, setCount] = useState<"one" | "many">("one");
  const [rows, setRows] = useState<Row[]>([newRow("r1", "cnpj")]);
  const [aceiteLgpd, setAceiteLgpd] = useState(false);
  const [done, setDone] = useState(false);

  // Aceite real do convite: cria o cadastro e seta os cookies de sessão.
  const accept = useMutation({
    mutationFn: (body: ClientAcceptPayload) =>
      invitationService.accept(token ?? "", body),
    onSuccess: () => setDone(true),
  });

  // "1 cadastro": mantém só a linha com algum dado (estado derivado no clique,
  // sem efeito lendo deps stale).
  const selectOne = () => {
    setCount("one");
    setRows((rs) =>
      rs.length > 1 ? [rs.find((r) => r.cnpj || r.razao || r.cpf || r.nome) || rs[0]] : rs,
    );
  };

  const updateRow = (id: string, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, newRow(`r${rs.length + 1}-${rs.length}`, "cnpj")]);
  const removeRow = (id: string) =>
    setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.id !== id) : rs));
  const setDocType = (id: string, docType: DocType) =>
    updateRow(id, {
      docType,
      autofilled: false,
      ...(docType === "cnpj"
        ? { nome: "", cpf: "" }
        : { cnpj: "", razao: "", fantasia: "", cidade: "", uf: "", cnae: "", regime: "" }),
    });

  const lookup = async (id: string) => {
    const row = rows.find((r) => r.id === id);
    if (!row || row.docType !== "cnpj" || !row.cnpj) return;
    updateRow(id, { loading: true });
    try {
      const r = await cnpjLookup.mutateAsync(row.cnpj);
      updateRow(id, {
        loading: false,
        autofilled: true,
        razao: row.razao || r.razao_social || "",
        fantasia: row.fantasia || r.nome_fantasia || "",
        cidade: row.cidade || r.cidade || "",
        uf: row.uf || r.uf || "",
        cnae: row.cnae || r.cnae || "",
        regime: row.regime || r.tax_regime || "",
        logradouro: row.logradouro || r.logradouro || "",
        numero: row.numero || r.numero || "",
        complemento: row.complemento || r.complemento || "",
        bairro: row.bairro || r.bairro || "",
        cep: row.cep || r.cep || "",
      });
    } catch {
      updateRow(id, { loading: false });
    }
  };

  const isValid = (r: Row) =>
    r.docType === "cnpj" ? !!(r.cnpj && r.razao) : !!(r.cpf && r.nome);
  const validRows = rows.filter(isValid);
  const canSubmit = validRows.length > 0 && aceiteLgpd && !!token && !accept.isPending;

  // O accept do backend é por convite (um cadastro). Enviamos o cadastro
  // principal — o primeiro válido — que é o vinculado ao convite.
  const submit = () => {
    const primary = validRows[0];
    if (!primary || !aceiteLgpd || !token) return;
    accept.mutate({
      document: stripDocument(primary.docType === "cnpj" ? primary.cnpj : primary.cpf),
      legal_name: primary.docType === "cnpj" ? primary.razao : primary.nome,
      tax_regime: primary.regime || null,
      address_street: primary.logradouro || null,
      address_number: primary.numero || null,
      address_complement: primary.complemento || null,
      address_neighborhood: primary.bairro || null,
      address_city: primary.cidade || null,
      address_state: primary.uf || null,
      address_zip: primary.cep || null,
      accepted_tos: aceiteLgpd,
      marketing_consent: false,
    });
  };
  const submitError = accept.isError ? extractErrorMessage(accept.error) : null;

  const Header = () => (
    <header className="onb-topbar">
      <div className="onb-brand">
        <span
          className="ws-mark"
          style={{ background: "linear-gradient(135deg, #F4CC04, #B49A02)", color: "#011D33" }}
        >
          TD
        </span>
        <div className="onb-brand-text">
          <div className="onb-brand-name">Tributo Devido</div>
          <div className="onb-brand-sub">Cadastro de empresa</div>
        </div>
      </div>
      <button className="onb-exit-btn" onClick={() => onExit?.()}>
        <Icon name="log-out" size={14} /> Salvar e sair
      </button>
    </header>
  );

  if (done) {
    // O accept persiste o cadastro principal (o vinculado ao convite).
    const persisted = validRows.slice(0, 1);
    return (
      <div className="onb-shell onb-shell-single">
        <Header />
        <main className="onb-content">
          <div className="onb-inner">
            <div className="onb-success">
              <div className="onb-success-icon">
                <Icon name="check" size={36} stroke={2.5} />
              </div>
              <h1 className="onb-step-title" style={{ textAlign: "center" }}>
                {persisted[0]?.docType === "cnpj" ? "Empresa adicionada!" : "Cadastro adicionado!"}
              </h1>
              <p className="onb-step-sub" style={{ textAlign: "center" }}>
                Vinculado à organização{" "}
                <strong style={{ color: "var(--fg1)" }}>{orgDisplayName}</strong>. Próximo passo:
                conceder procuração e-CAC para cada CNPJ.
              </p>

              <div className="onb-success-checklist" style={{ marginTop: 28 }}>
                <div className="onb-success-checklist-head">
                  <div>
                    <div className="onb-section-title" style={{ marginBottom: 2 }}>
                      Empresas cadastradas
                    </div>
                    <div className="onb-section-sub">Pronto pra operar após procuração.</div>
                  </div>
                  <span
                    className="tag-chip"
                    style={{ background: "var(--surface-2)", color: "var(--fg2)" }}
                  >
                    {persisted.length}
                  </span>
                </div>
                <div className="onb-success-list">
                  {persisted.map((r) => (
                    <div key={r.id} className="onb-success-item is-completed">
                      <span className="onb-success-bullet ok">
                        <Icon name="check" size={13} stroke={3} />
                      </span>
                      <div style={{ flex: 1 }}>
                        <div className="onb-success-item-title">
                          {r.docType === "cnpj" ? r.razao : r.nome}
                          <span className="emp-doc-pill" data-kind={r.docType}>
                            {r.docType === "cnpj" ? "PJ" : "PF"}
                          </span>
                        </div>
                        <div className="onb-success-item-sub">
                          {r.docType === "cnpj" ? r.cnpj : r.cpf}
                          {r.cidade && ` · ${r.cidade}`}
                          {r.uf && ` / ${r.uf}`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="onb-success-foot">
                <Button variant="primary" iconRight="arrow-up-right" onClick={() => onExit?.()}>
                  Ir para o dashboard
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="onb-shell onb-shell-single">
      <Header />
      <main className="onb-content">
        <div className="onb-inner">
          <div className="onb-ws-banner">
            <span className="onb-ws-banner-icon">
              <Icon name="layers" size={14} />
            </span>
            <div>
              <div className="onb-ws-banner-l">PERTENCE À ORGANIZAÇÃO</div>
              <div className="onb-ws-banner-v">{orgDisplayName}</div>
            </div>
            <span className="onb-ws-banner-lock">
              <Icon name="shield-check" size={11} /> Vinculado
            </span>
          </div>

          <div className="onb-section-head">
            <div className="onb-step-eyebrow">CADASTRO DE EMPRESA · ÚNICA ETAPA</div>
            <h1 className="onb-step-title">Cadastre seus CNPJs ou CPFs</h1>
            <p className="onb-step-sub">
              A organização e a procuração e-CAC já estão configuradas — basta informar os
              documentos que você quer adicionar ao grupo. Aceitamos <strong>CNPJ</strong> (PJ) e{" "}
              <strong>CPF</strong> (PF). Para CNPJ,{" "}
              <strong>razão social, endereço, CNAE e regime</strong> são preenchidos
              automaticamente pela Receita.
            </p>
          </div>

          <div className="emp-count-toggle">
            <button
              className={`emp-count-card ${count === "one" ? "is-on" : ""}`}
              onClick={selectOne}
            >
              <span className="emp-count-icon">
                <Icon name="building" size={14} />
              </span>
              <div>
                <div className="emp-count-title">1 cadastro</div>
                <div className="emp-count-sub">Adicionar um único CNPJ ou CPF.</div>
              </div>
            </button>
            <button
              className={`emp-count-card ${count === "many" ? "is-on" : ""}`}
              onClick={() => setCount("many")}
            >
              <span className="emp-count-icon">
                <Icon name="layers" size={14} />
              </span>
              <div>
                <div className="emp-count-title">Vários cadastros</div>
                <div className="emp-count-sub">Adicione quantos precisar de uma vez só.</div>
              </div>
            </button>
          </div>

          <div className="emp-rows">
            {rows.map((row, idx) => {
              const isPj = row.docType === "cnpj";
              const hasInput = isPj ? !!row.cnpj : !!row.cpf;
              const committed = row.autofilled || hasInput;
              return (
                <div key={row.id} className="onb-section emp-row">
                  <div className="emp-row-line">
                    <span className="emp-row-num">#{idx + 1}</span>

                    {!committed && (
                      <div className="emp-doc-toggle" role="tablist" aria-label="Tipo de cadastro">
                        <button
                          type="button"
                          role="tab"
                          aria-selected={isPj}
                          className={`emp-doc-tab ${isPj ? "is-on" : ""}`}
                          onClick={() => setDocType(row.id, "cnpj")}
                        >
                          <Icon name="building" size={12} /> CNPJ{" "}
                          <span className="emp-doc-tab-tag">PJ</span>
                        </button>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={!isPj}
                          className={`emp-doc-tab ${!isPj ? "is-on" : ""}`}
                          onClick={() => setDocType(row.id, "cpf")}
                        >
                          <Icon name="user" size={12} /> CPF{" "}
                          <span className="emp-doc-tab-tag">PF</span>
                        </button>
                      </div>
                    )}

                    <div className="emp-row-field">
                      <label className="emp-row-field-label">
                        {isPj ? "CNPJ" : "CPF"} <span className="onb-field-req">*</span>
                      </label>
                      <div className="onb-input-wrap">
                        <Icon name={isPj ? "building" : "user"} size={14} />
                        {isPj ? (
                          <input
                            className="onb-input bare"
                            placeholder="00.000.000/0001-00"
                            inputMode="text"
                            maxLength={18}
                            value={row.cnpj}
                            onChange={(e) =>
                              updateRow(row.id, {
                                cnpj: formatCnpj(e.target.value),
                                autofilled: false,
                              })
                            }
                            onBlur={() => void lookup(row.id)}
                          />
                        ) : (
                          <input
                            className="onb-input bare"
                            placeholder="000.000.000-00"
                            inputMode="numeric"
                            maxLength={14}
                            value={row.cpf}
                            onChange={(e) => updateRow(row.id, { cpf: formatCpf(e.target.value) })}
                          />
                        )}
                        {row.autofilled && (
                          <span className="emp-row-tag">
                            <Icon name="check" size={11} stroke={3} /> AUTO
                          </span>
                        )}
                      </div>
                      <div className="onb-field-hint">
                        {isPj
                          ? row.loading
                            ? "consultando Receita Federal..."
                            : row.autofilled
                              ? "razão social, CNAE e regime preenchidos pela Receita"
                              : "razão social, CNAE e regime são preenchidos automaticamente"
                          : "pessoa física — preenchimento manual"}
                      </div>
                    </div>

                    {count === "many" && rows.length > 1 && (
                      <button
                        className="row-act emp-row-remove"
                        onClick={() => removeRow(row.id)}
                        aria-label="Remover"
                      >
                        <Icon name="x" size={14} />
                      </button>
                    )}
                  </div>

                  {!isPj && row.cpf && (
                    <div className="onb-field" style={{ marginTop: 12 }}>
                      <label className="onb-field-label">
                        Nome completo <span className="onb-field-req">*</span>
                      </label>
                      <input
                        className="onb-input"
                        maxLength={120}
                        placeholder="Nome conforme CPF"
                        value={row.nome}
                        onChange={(e) => updateRow(row.id, { nome: e.target.value })}
                      />
                    </div>
                  )}

                  {(row.autofilled || (isPj ? row.razao : row.nome && row.cpf)) && (
                    <div className="emp-row-preview">
                      <div className="emp-row-preview-title">
                        {isPj ? row.razao : row.nome}
                        {!isPj && (
                          <span className="emp-doc-pill" data-kind="cpf">
                            PF
                          </span>
                        )}
                      </div>
                      {isPj && row.cidade && (
                        <div className="emp-row-meta">
                          <span>
                            <Icon name="map-pin" size={11} /> {row.cidade}
                            {row.uf ? ` / ${row.uf}` : ""}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {count === "many" && (
              <button className="btn btn-ghost emp-add-row" onClick={addRow}>
                <Icon name="plus" size={14} /> Adicionar outro cadastro
              </button>
            )}
          </div>

          <div className="onb-section onb-section-terms" style={{ marginTop: 8 }}>
            <label className="onb-checkbox">
              <span
                className={`onb-checkbox-box ${aceiteLgpd ? "is-on" : ""}`}
                onClick={() => setAceiteLgpd(!aceiteLgpd)}
              >
                {aceiteLgpd && <Icon name="check" size={12} stroke={3} />}
              </span>
              <span className="onb-checkbox-text">
                Autorizo o Tributo Devido a armazenar e processar os dados informados conforme a{" "}
                <a href="#" className="onb-link">
                  LGPD (Lei 13.709/2018)
                </a>
                .
              </span>
            </label>
          </div>

          {submitError && (
            <div className="onb-submit-error" role="alert">
              <Icon name="alert-triangle" size={14} />
              <span>{submitError}</span>
            </div>
          )}

          <div className="onb-foot">
            <div className="onb-foot-left" />
            <div className="onb-foot-right">
              <Button variant="primary" iconRight="check" onClick={submit} disabled={!canSubmit}>
                {accept.isPending ? "Cadastrando…" : "Cadastrar"}
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
